/*
 * � 2025, NTT Data Group. All rights reserved. See LICENSE.txt file in the project root for third party licenses info.
 */

import { of, throwError } from "rxjs";
import { HttpRequestOptions } from "../../../../models/ui/http-request-options.model";
import { HttpParams } from "@angular/common/http";
import {
	GET_DETAILS_RESPONSE,
	GET_EMISSIONS_SHARE_RESPONSE,
	GET_FORECAST_RESPONSE,
	GET_SUMMARY_RESPONSE
} from "./dashboard-cio.db";
import { ApplicationError } from "../../../../models/ui/application-error.model";

export class DashboardCio {
	public static getSummary(options: HttpRequestOptions) {
		const { params } = options;

		const httpParams = params as HttpParams;

		if (!httpParams)
			return throwError(
				() =>
					new ApplicationError(
						"No params provided",
						this.constructor.name,
						this.getSummary.name
					)
			);

		const startDate = httpParams.get("startDate");

		if (!startDate)
			return throwError(
				() =>
					new ApplicationError(
						"Missing required parameters",
						this.constructor.name,
						this.getSummary.name
					)
			);

		return of({
			totalEmissions: GET_SUMMARY_RESPONSE.totalCO2.value,
			operationalEmissions: GET_SUMMARY_RESPONSE.scopeKpis[1]?.value ?? 0,
			embodiedEmissions: GET_SUMMARY_RESPONSE.scopeKpis[2]?.value ?? 0,
			houses: 1450,
			kmCarDrive: 128900
		});
	}

	public static getForecast(options: HttpRequestOptions) {
		const { params } = options;

		const httpParams = params as HttpParams;

		if (!httpParams)
			return throwError(
				() =>
					new ApplicationError(
						"No params provided",
						this.constructor.name,
						this.getForecast.name
					)
			);

		const startDate = httpParams.get("startDate");
		const mode = httpParams.get("mode");

		if (!startDate || !mode)
			return throwError(
				() =>
					new ApplicationError(
						"Missing required parameters",
						this.constructor.name,
						this.getForecast.name
					)
			);

		const modeKey = DashboardCio._mapModeToForecastKey(mode);
		const forecast = GET_FORECAST_RESPONSE[modeKey];

		return of(DashboardCio._mapForecastToTrendLine(forecast));
	}

	public static getEmissionsShare(options: HttpRequestOptions) {
		const { params } = options;

		const httpParams = params as HttpParams;

		if (!httpParams)
			return throwError(
				() =>
					new ApplicationError(
						"No params provided",
						this.constructor.name,
						this.getEmissionsShare.name
					)
			);

		const startDate = httpParams.get("startDate");
		const mode = httpParams.get("mode");

		if (!startDate || !mode)
			return throwError(
				() =>
					new ApplicationError(
						"Missing required parameters",
						this.constructor.name,
						this.getEmissionsShare.name
					)
			);

		const modeKey = DashboardCio._mapModeToForecastKey(mode);
		return of(GET_EMISSIONS_SHARE_RESPONSE[modeKey]);
	}

	public static getDetails(options: HttpRequestOptions) {
		const { params } = options;

		const httpParams = params as HttpParams;

		if (!httpParams)
			return throwError(
				() =>
					new ApplicationError(
						"No params provided",
						this.constructor.name,
						this.getDetails.name
					)
			);

		const startDate = httpParams.get("startDate");
		const scope = httpParams.get("scope");

		if (!startDate || !scope)
			return throwError(
				() =>
					new ApplicationError(
						"Missing required parameters",
						this.constructor.name,
						this.getDetails.name
					)
			);

		return of(DashboardCio._mapDetailsToScopeBreakdown(scope));
	}

	private static _mapModeToForecastKey(mode: string): "TYPE" | "SCOPE" {
		const normalized = mode.toLowerCase();
		return normalized === "domain" ? "TYPE" : "SCOPE";
	}

	private static _mapForecastToTrendLine(forecast: {
		title: string;
		series: { name: string; type: string; values: unknown[] }[];
	}) {
		const barSeries = forecast.series
			.filter(serie => serie.type === "bar")
			.map(serie => ({
				name: serie.name,
				bars: serie.values
			}));

		const lineSerie = forecast.series.find(serie => serie.type === "line");
		const trend = lineSerie
			? {
					name: lineSerie.name,
					linePoints: lineSerie.values
				}
			: { name: "Trend", linePoints: [] };

		return {
			trend,
			bars: {
				title: forecast.title,
				barSeries
			}
		};
	}

	private static _mapDetailsToScopeBreakdown(scope: string) {
		const normalizedScope = scope.toLowerCase();
		const scopeLabel = normalizedScope.startsWith("scope")
			? `Scope ${normalizedScope.replace("scope", "")}`.trim()
			: scope;

		const details = GET_DETAILS_RESPONSE.TYPE?.details || [];
		const barSeries = details
			.map(detail => {
				const matchedSerie = detail.barSeries.find(
					serie => serie.name.toLowerCase() === scopeLabel.toLowerCase()
				);
				return matchedSerie
					? {
							name: detail.title,
							bars: matchedSerie.bars
						}
					: null;
			})
			.filter(Boolean);

		return {
			title: scopeLabel,
			barSeries
		};
	}
}
